import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Calendar, Clock, ArrowRight } from "lucide-react";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { siteImages } from "@/lib/siteData";
import { trackEvent } from "@/lib/analytics";

// Campaign attribution for clicks coming from the maternity outfit guide.
// `getAttribution()` reads these UTM params on landing, persists them, and
// passes them into Stripe checkout metadata so we can correlate the
// completed-payment event back to this CTA.
const CAMPAIGN = "maternity_outfit_guide";
const ctaHref = (path: "/book" | "/availability", placement: string) => {
  const u = new URLSearchParams({
    utm_source: "blog",
    utm_medium: "cta",
    utm_campaign: CAMPAIGN,
    utm_content: placement,
  });
  return `${path}?${u.toString()}`;
};
const fireCta = (placement: string, destination: "/book" | "/availability") => {
  trackEvent("maternity_cta_click", {
    campaign: CAMPAIGN,
    placement,
    destination,
    slug: "maternity-shoot-outfit-guide",
  });
};

// Stable anchor ids — exposed for validation tests so we can assert each
// question appears exactly once in faqSchema() output.
const slugify = (s: string) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 60);

const FAQS = [
  {
    q: "What is the best color to wear for a maternity photoshoot?",
    a: "Earth tones (rust, sage, camel, cream), deep jewel tones (emerald, sapphire, plum), and soft neutrals all photograph beautifully against MyStudio's dark and cyc backdrops. Avoid bright whites and busy patterns that pull attention away from your bump.",
  },
  {
    q: "Should I wear a tight or loose dress for maternity photos?",
    a: "Bring both. Form-fitting dresses (bodycon, ruched, wrap) emphasize your bump shape, while flowy maxi gowns add movement and romance. Most clients change once mid-session to capture both looks.",
  },
  {
    q: "What should my partner wear in maternity photos?",
    a: "Coordinate, don't match. Pair a neutral solid shirt (white tee, black henley, denim) with your dress's color family. Avoid logos, busy prints, and competing bold colors so the focus stays on the bump.",
  },
  {
    q: "How many outfits should I bring to my maternity session?",
    a: "Two outfits is the sweet spot for a 60-minute session: one fitted look and one flowy gown. Bring a third if you want a casual loungewear or partner-coordinated set.",
  },
  {
    q: "What fabrics photograph best under studio lighting?",
    a: "Chiffon, jersey knit, tulle, and velvet all photograph beautifully. Avoid heavy linen (wrinkles fast), shiny satin (hot spots under strobes), and sequins (distracting reflections).",
  },
  {
    q: "When in pregnancy should I do my maternity shoot?",
    a: "Between 30 and 34 weeks is ideal — your bump is fully visible but you're still comfortable enough to pose. Book your session as early as 20 weeks to lock in your preferred date.",
  },
];

export const MATERNITY_FAQS = FAQS.map((f) => ({ ...f, id: slugify(f.q) }));
export function maternityFaqSchema() {
  const seen = new Set<string>();
  const mainEntity = MATERNITY_FAQS
    .filter((f) => (seen.has(f.id) ? false : (seen.add(f.id), true)))
    .map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    }));
  return { "@context": "https://schema.org", "@type": "FAQPage", mainEntity };
}

export function MaternityOutfitGuideCta({ variant = "inline" }: { variant?: "inline" | "sticky" }) {
  if (variant === "sticky") {
    return (
      <div className="fixed bottom-0 inset-x-0 z-40 lg:hidden bg-background/95 backdrop-blur border-t border-primary/30 p-3">
        <Link to={ctaHref("/book", "sticky_mobile")} className="block" onClick={() => fireCta("sticky_mobile", "/book")}>
          <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-body font-semibold">
            Book Your Maternity Session <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      </div>
    );
  }
  return (
    <div className="my-10 p-6 md:p-8 bg-primary/10 border border-primary/30 grid gap-4 md:grid-cols-[1fr_auto] items-center">
      <div>
        <p className="text-xs font-mono uppercase tracking-[0.25em] text-primary mb-2">Ready to Book?</p>
        <h3 className="font-display tracking-widest text-xl md:text-2xl mb-1">
          BOOK YOUR MATERNITY SESSION
        </h3>
        <p className="text-sm text-muted-foreground font-body">
          Studio A and B available 7 days a week. Lock in your bump-window date now.
        </p>
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <Link to={ctaHref("/book", "inline_primary")} onClick={() => fireCta("inline_primary", "/book")}>
          <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-body font-semibold">
            <Calendar className="mr-2 h-4 w-4" /> Book Now
          </Button>
        </Link>
        <Link to={ctaHref("/availability", "inline_secondary")} onClick={() => fireCta("inline_secondary", "/availability")}>
          <Button variant="outline" className="w-full font-body">
            <Clock className="mr-2 h-4 w-4" /> View Open Times
          </Button>
        </Link>
      </div>
    </div>
  );
}

export function MaternityOutfitGuideFaq() {
  return (
    <section className="my-12 not-prose" id="maternity-outfit-faqs">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(maternityFaqSchema())}</script>
      </Helmet>
      <h2 className="text-2xl md:text-3xl font-display tracking-widest mb-6 text-foreground">
        MATERNITY OUTFIT FAQS
      </h2>
      <Accordion type="single" collapsible className="bg-panel border border-border">
        {MATERNITY_FAQS.map((f) => (
          <AccordionItem
            key={f.id}
            id={`faq-${f.id}`}
            value={f.id}
            className="border-border last:border-b-0 px-5"
          >
            <AccordionTrigger className="text-left font-display tracking-wider text-base hover:text-primary">
              {f.q}
            </AccordionTrigger>
            <AccordionContent className="text-muted-foreground font-body">{f.a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  );
}

export function MoreMaternityTips({ currentSlug }: { currentSlug: string }) {
  const [posts, setPosts] = useState<any[]>([]);
  useEffect(() => {
    supabase
      .from("blog_posts")
      .select("id,slug,title,excerpt,cover_image")
      .eq("status", "published")
      .or(
        "slug.ilike.%maternity%,seo_title.ilike.%maternity%,title.ilike.%maternity%"
      )
      .neq("slug", currentSlug)
      .limit(3)
      .then(({ data }) => setPosts(data ?? []));
  }, [currentSlug]);

  if (!posts.length) return null;
  return (
    <section className="my-12 not-prose" id="more-maternity-tips">
      <div className="flex items-end justify-between mb-6">
        <h2 className="text-2xl md:text-3xl font-display tracking-widest text-foreground">
          MORE MATERNITY TIPS
        </h2>
        <Link to="/blog" className="text-xs font-mono uppercase tracking-[0.2em] text-primary hover:underline">
          All posts →
        </Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {posts.map((p) => (
          <Link
            key={p.id}
            to={`/blog/${p.slug}`}
            className="group bg-panel border border-border overflow-hidden hover:glow-amber transition-all"
          >
            <div className="aspect-video bg-muted overflow-hidden">
              <img
                src={p.cover_image || siteImages.heroSlider1}
                alt={p.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                loading="lazy"
              />
            </div>
            <div className="p-4">
              <h4 className="font-display tracking-wider text-sm group-hover:text-primary transition-colors mb-2">
                {p.title}
              </h4>
              {p.excerpt && (
                <p className="text-xs text-muted-foreground font-body line-clamp-2">{p.excerpt}</p>
              )}
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}